import React, { useEffect, useCallback, useRef } from 'react';
import { Button, Modal, Alert, Spin, Typography, Table } from '../';
import { useNotificationContext } from '../../providers/NotificationProvider';
import { createHeaderTableColumns } from './utils/tableUtils';
import { PreflightComponent } from './components/PreflightComponent';
import { usePreflightMonitor } from './hooks/usePreflightMonitor';
import { useCsvDownload } from '../../hooks/useCsvDownload';
import './PreflightMonitor.scss';

const { Title, Text } = Typography;

/**
 * Refactored Preflight Monitor component using modular architecture
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Handler for modal close
 * @param {Function} onComplete - Handler for when preflights complete successfully
 * @param {Array} preflightRequests - Array of preflight request objects
 * @param {string} title - Modal title
 * @param {string} loadingMessage - Loading message to display
 * @param {boolean} proceedOnSuccess - Whether to auto-proceed when all preflights succeed
 * @param {Object} migrationContext - Migration context data
 * @param {boolean} inspectSourcesMode - Whether this is source inspection mode
 * @param {string} continueButtonText - Custom text for the continue button
 * @param {boolean} hideCsvDownload - Hide the "Download Migration CSV" link
 */
export const PreflightMonitor = ({
    open,
    onClose,
    onComplete,
    preflightRequests = [],
    title = 'Migration Readiness Check',
    loadingMessage = 'Initiating preflights...',
    proceedOnSuccess = false,
    migrationContext = {},
    inspectSourcesMode = false,
    continueButtonText,
    hideCsvDownload = false,
}) => {
    const autoProceedOnSuccessRef = useRef(false);
    const { notifySuccess, notifyError, notifyWarning } = useNotificationContext();
    const { isDownloadingCsv, triggerCsvDownload } = useCsvDownload();

    // Use the custom hook for all the logic
    const {
        // State
        preflightComponents,
        groupedComponents,
        allComplete,
        allSuccess,
        fatalError,
        error,
        showWarningDescription,
        showFatalErrorDescription,
        selectedRowKeys,
        expandedComponents,
        selectAll,
        nonErrorsHiddenPerComponent,
        completionNotified,
        isLoading,
        currentStep,

        // Actions
        setSelectedRowKeys,
        setExpandedComponents,
        setSelectAll,
        setNonErrorsHiddenPerComponent,
        setCompletionNotified,
        handleErrorWarningRecovery,
        getRecoveryState,
    } = usePreflightMonitor({
        open,
        preflightRequests,
        migrationContext,
        inspectSourcesMode,
        onComplete,
    });

    useEffect(() => {
        if (!open) {
            autoProceedOnSuccessRef.current = false;
        }
    }, [open]);

    // Auto-proceed logic (matches Backbone implementation)
    useEffect(() => {
        if (allComplete && allSuccess && proceedOnSuccess && !autoProceedOnSuccessRef.current) {
            autoProceedOnSuccessRef.current = true;
            setTimeout(() => {
                onComplete?.({
                    success: true,
                    preflights: preflightComponents,
                });
            }, 250);
        }
    }, [allComplete, allSuccess, proceedOnSuccess, onComplete, preflightComponents]);

    // Auto-select successful and warning components (only in migration readiness mode)
    useEffect(() => {
        if (allComplete && !inspectSourcesMode) {
            const ignoreValidationErrors = migrationContext?.ignoreValidationErrors || false;
            const selectableKeys = preflightComponents
                .filter((c) => {
                    const isNotTargetCloud = c.type !== 'target_cloud';
                    const isSuccessOrWarning =
                        c.state === 'success' || (c.state === 'error' && c.errorType === 'warning');
                    const isFailedButIgnored =
                        ignoreValidationErrors && c.state === 'error' && c.errorType !== 'warning';

                    return isNotTargetCloud && (isSuccessOrWarning || isFailedButIgnored);
                })
                .map((c) => c.resource_id);
            setSelectedRowKeys(selectableKeys);
        }
    }, [
        allComplete,
        preflightComponents,
        inspectSourcesMode,
        migrationContext?.ignoreValidationErrors,
        setSelectedRowKeys,
    ]);

    // Handle completion notifications
    useEffect(() => {
        if (allComplete && !completionNotified) {
            setCompletionNotified(true);

            if (allSuccess) {
                notifySuccess({
                    description: 'All preflights completed successfully',
                });
            } else if (fatalError) {
                notifyError({
                    description: 'Some preflights failed with fatal errors',
                });
            } else if (showWarningDescription) {
                notifyWarning({
                    description: 'Some preflights completed with warnings',
                });
            }
        }
    }, [
        allComplete,
        allSuccess,
        fatalError,
        showWarningDescription,
        completionNotified,
        notifySuccess,
        notifyError,
        notifyWarning,
        setCompletionNotified,
    ]);

    // Component interaction handlers
    const handleComponentExpand = useCallback(
        (resourceId) => {
            setExpandedComponents((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(resourceId)) {
                    newSet.delete(resourceId);
                } else {
                    newSet.add(resourceId);
                }
                return newSet;
            });
        },
        [setExpandedComponents],
    );

    const handleExpandAll = useCallback(() => {
        const allResourceIds = preflightComponents.map((c) => c.resource_id);
        if (expandedComponents.size === allResourceIds.length) {
            setExpandedComponents(new Set());
        } else {
            setExpandedComponents(new Set(allResourceIds));
        }
    }, [preflightComponents, expandedComponents, setExpandedComponents]);

    const handleSelectAll = useCallback(
        (checked) => {
            setSelectAll(checked);
            if (checked) {
                const ignoreValidationErrors = migrationContext?.ignoreValidationErrors || false;
                const selectableKeys = preflightComponents
                    .filter((c) => {
                        const isNotTargetCloud = c.type !== 'target_cloud';
                        const isSuccessOrWarning =
                            c.state === 'success' || (c.state === 'error' && c.errorType === 'warning');
                        const isFailedButIgnored =
                            ignoreValidationErrors && c.state === 'error' && c.errorType !== 'warning';

                        return isNotTargetCloud && (isSuccessOrWarning || isFailedButIgnored);
                    })
                    .map((c) => c.resource_id);
                setSelectedRowKeys(selectableKeys);
            } else {
                setSelectedRowKeys([]);
            }
        },
        [preflightComponents, migrationContext?.ignoreValidationErrors, setSelectAll, setSelectedRowKeys],
    );

    const handleRowSelection = useCallback(
        (resourceId, checked) => {
            setSelectedRowKeys((prev) => {
                if (checked) {
                    return [...prev, resourceId];
                } else {
                    return prev.filter((key) => key !== resourceId);
                }
            });
        },
        [setSelectedRowKeys],
    );

    const handleViewAllToggle = useCallback(
        (componentId) => {
            const currentlyHidden = nonErrorsHiddenPerComponent.get(componentId) === true;
            setNonErrorsHiddenPerComponent((prev) => {
                const newMap = new Map(prev);
                newMap.set(componentId, !currentlyHidden);
                return newMap;
            });
        },
        [nonErrorsHiddenPerComponent, setNonErrorsHiddenPerComponent],
    );

    // Handle continue button click
    const handleContinue = useCallback(() => {
        const selectedResources = selectedRowKeys;
        const selectedComponents = preflightComponents.filter((c) => selectedResources.includes(c.resource_id));

        onComplete?.({
            success: true,
            selectedResources,
            preflights: selectedComponents,
            warnings: selectedComponents.filter((c) => c.errorType === 'warning'),
        });
    }, [selectedRowKeys, preflightComponents, onComplete]);

    // Determine if continue button should be enabled (only in migration readiness mode)
    const canContinue = !inspectSourcesMode && allComplete && selectedRowKeys.length > 0;

    // Get current loading message
    const getCurrentLoadingMessage = () => {
        if (currentStep === 'context') {
            return 'Updating migration context...';
        }
        return loadingMessage;
    };

    // Handle CSV download based on current preflight results
    const handleCsvDownload = useCallback(() => {
        if (!migrationContext.cloudAccount || !allComplete) {
            notifyError({ description: 'Cannot download CSV: Migration context or preflights not ready' });
            return;
        }

        // Get the successful source IDs from preflight results
        const successfulSources = preflightComponents
            .filter(
                (comp) =>
                    comp.type === 'source' &&
                    (comp.state === 'success' || (comp.state === 'error' && comp.errorType === 'warning')),
            )
            .map((comp) => comp.resource_id)
            .filter((id) => id); // Remove any undefined IDs

        if (successfulSources.length === 0) {
            notifyError({ description: 'No successful sources available for CSV download' });
            return;
        }

        // Create CSV download payload similar to Backbone logic
        const csvDownloadPayload = {
            cloud_account: migrationContext.cloudAccount.id || migrationContext.targetAccount,
            cloud_type: migrationContext.cloudAccount.cloud_type || migrationContext.cloud_type,
            appliance_id: migrationContext.cloudAccount.appliance?.id || migrationContext.applianceId,
            sources: successfulSources,
            migration_method: 'os-based', // This would be determined based on source types
        };

        triggerCsvDownload(csvDownloadPayload);
    }, [migrationContext, allComplete, preflightComponents, triggerCsvDownload, notifyError]);

    // Create header table columns using the utility function
    const headerColumns = createHeaderTableColumns({
        inspectSourcesMode,
        allComplete,
        expandedComponents,
        preflightComponents,
        selectAll,
        onExpandAll: handleExpandAll,
        onSelectAll: handleSelectAll,
    });

    return (
        <Modal
            title={migrationContext?.in_situ ? 'Modernization Readiness Check' : title}
            open={open}
            onCancel={onClose}
            width={900}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>,
                // Continue button only appears in migration readiness mode
                ...(!inspectSourcesMode && canContinue
                    ? [
                        <Button key="continue" type="primary" onClick={handleContinue}>
                            {continueButtonText ||
                                (migrationContext?.in_situ
                                    ? 'Create Modernization Profile'
                                    : 'Create Migration Profile')}
                        </Button>,
                    ]
                    : []),
            ]}
            className="preflight-monitor"
        >
            <div className="preflight-content">
                {error ? (
                    <Alert title="Preflight Error" description={error} type="error" showIcon className="error-alert" />
                ) : (
                    <>
                        {isLoading && (
                            <div className="loading-container">
                                <Spin size="large" />
                                <Text className="loading-text">{getCurrentLoadingMessage()}</Text>
                            </div>
                        )}

                        {preflightComponents.length > 0 && (
                            <>
                                {/* Header with expand all and select all controls */}
                                <div className="preflight-header">
                                    <Table
                                        size="small"
                                        pagination={false}
                                        showHeader={false}
                                        dataSource={[{}]}
                                        columns={headerColumns}
                                        rowKey={() => 'header'}
                                    />
                                </div>

                                {/* Render grouped components using the new modular component */}
                                <div className="preflight-components">
                                    {Object.entries(groupedComponents).map(([type, components]) => (
                                        <div key={type} className="component-group">
                                            {components.map((component) => (
                                                <PreflightComponent
                                                    key={component.resource_id}
                                                    component={component}
                                                    inspectSourcesMode={inspectSourcesMode}
                                                    allComplete={allComplete}
                                                    expandedComponents={expandedComponents}
                                                    selectedRowKeys={selectedRowKeys}
                                                    nonErrorsHiddenPerComponent={nonErrorsHiddenPerComponent}
                                                    onComponentExpand={handleComponentExpand}
                                                    onRowSelection={handleRowSelection}
                                                    onViewAllToggle={handleViewAllToggle}
                                                    onRecoveryChange={handleErrorWarningRecovery}
                                                    getRecoveryState={getRecoveryState}
                                                    migrationContext={migrationContext}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                </div>

                                {showWarningDescription && (
                                    <Alert
                                        title="Warning"
                                        description="A warning status means migration is at risk without user action. If you have already taken recommended actions, you can manually select the component(s) and continue."
                                        type="warning"
                                        showIcon
                                        className="warning-alert"
                                    />
                                )}

                                {showFatalErrorDescription && (
                                    <Alert
                                        title="Fatal Error"
                                        description="A process failed that must pass in order to continue. Please review the error, cancel this process, make corrections, then try again."
                                        type="error"
                                        showIcon
                                        className="fatal-error-alert"
                                    />
                                )}

                                {/* Download Migration CSV Link - show when Create Migration Profile button is enabled */}
                                {!inspectSourcesMode && canContinue && !hideCsvDownload && (
                                    <div className="csv-download-section">
                                        <Button
                                            type={'link'}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleCsvDownload();
                                            }}
                                        >
                                            {isDownloadingCsv ? 'Generating CSV...' : 'Download Migration CSV'}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};
